/**
 * ZO Bull Algo Trading Platform
 * Version: 6.2.0
 */
/**
 * AI Copilot Page
 * Full-screen chat interface for ZO Bull AI Copilot
 * 
 * Features:
 * - 274+ Technical Indicators
 * - 60+ Candlestick Patterns
 * - Options Trading (PCR, Greeks, Multi-leg)
 * - Multi-language support
 * - TradetrON Integration
 */

import React, { useState, useRef, useEffect } from 'react';
import '../components/CopilotChat.css'; // Reuse the chat styles

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}

// Trading knowledge base - ENHANCED with 274+ indicators, 60+ patterns
const TRADING_KB = {
  indicators: {
    // Momentum (23)
    rsi: { name: 'RSI', desc: 'Momentum oscillator (0-100). >70 overbought, <30 oversold', params: 'Period: 14' },
    stoch: { name: 'Stochastic', desc: 'Momentum indicator comparing close to range', params: 'K: 14, D: 3' },
    stoch_rsi: { name: 'Stochastic RSI', desc: 'Stochastic applied to RSI values', params: 'Length: 14' },
    macd: { name: 'MACD', desc: 'Trend-following momentum indicator', params: 'Fast: 12, Slow: 26, Signal: 9' },
    cci: { name: 'CCI', desc: 'Commodity Channel Index - momentum oscillator', params: 'Period: 20' },
    momentum: { name: 'Momentum', desc: 'Rate of price change', params: 'Period: 10' },
    roc: { name: 'ROC', desc: 'Rate of Change - percentage price change', params: 'Period: 12' },
    williams_r: { name: 'Williams %R', desc: 'Overbought/oversold oscillator (-100 to 0)', params: 'Period: 14' },
    ultimate_osc: { name: 'Ultimate Oscillator', desc: 'Multi-timeframe momentum', params: 'Periods: 7, 14, 28' },
    aroon: { name: 'Aroon', desc: 'Trend changes and strength', params: 'Period: 25' },
    awesome_osc: { name: 'Awesome Oscillator', desc: 'Momentum indicator using medians', params: 'Fast: 5, Slow: 34' },
    kst: { name: 'Know Sure Thing', desc: 'Collected rate of change oscillator', params: 'ROC periods: 10,15,20,30' },
    mfi: { name: 'Money Flow Index', desc: 'Volume-weighted RSI', params: 'Period: 14' },
    chaikin_osc: { name: 'Chaikin Oscillator', desc: 'Accumulation/distribution momentum', params: 'Fast: 3, Slow: 10' },
    ppo: { name: 'PPO', desc: 'Percentage Price Oscillator - MACD variant', params: 'Fast: 12, Slow: 26' },
    tsi: { name: 'True Strength Index', desc: 'Double-smoothed momentum', params: 'Long: 25, Short: 13' },
    rvi: { name: 'Relative Vigor Index', desc: 'Trend strength indicator', params: 'Period: 10' },
    cmo: { name: 'Chande Momentum Oscillator', desc: 'Momentum without smoothing', params: 'Period: 14' },
    dpo: { name: 'Detrended Price Oscillator', desc: 'Price oscillation without trend', params: 'Period: 14' },
    ema: { name: 'EMA', desc: 'Exponential Moving Average - weights recent prices', params: 'Period: 9, 21, 50, 200' },
    sma: { name: 'SMA', desc: 'Simple Moving Average - basic trend indicator', params: 'Period: 20, 50, 200' },
    wma: { name: 'WMA', desc: 'Weighted Moving Average', params: 'Period: 20' },
    vwma: { name: 'VWMA', desc: 'Volume Weighted Moving Average', params: 'Period: 20' },
    
    // Trend (25)
    supertrend: { name: 'Supertrend', desc: 'Trend direction with ATR. Green=Buy, Red=Sell', params: 'Period: 10, Multiplier: 3' },
    adx: { name: 'ADX', desc: 'Average Directional Index - trend strength (0-100)', params: 'Period: 14' },
    psar: { name: 'Parabolic SAR', desc: 'Stop and Reverse indicator', params: 'Start: 0.02, Max: 0.2' },
    ichimoku: { name: 'Ichimoku Cloud', desc: 'Trend direction and support/resistance', params: 'Tenkan: 9, Kijun: 26' },
    vwap: { name: 'VWAP', desc: 'Volume Weighted Average Price', params: 'None (anchor optional)' },
    dema: { name: 'DEMA', desc: 'Double Exponential Moving Average', params: 'Period: 21' },
    tema: { name: 'TEMA', desc: 'Triple Exponential Moving Average', params: 'Period: 21' },
    trix: { name: 'TRIX', desc: 'Triple smoothed rate of change', params: 'Period: 15' },
    alma: { name: 'ALMA', desc: 'Arnaud Legoux Moving Average', params: 'Period: 20, Offset: 0.85' },
    kama: { name: 'KAMA', desc: 'Kaufman Adaptive Moving Average', params: 'Period: 10' },
    vortex: { name: 'Vortex Indicator', desc: 'Trend reversal detection', params: 'Period: 14' },
    hull_ma: { name: 'Hull MA', desc: 'Low-lag moving average', params: 'Period: 20' },
    
    // Volatility (15)
    atr: { name: 'ATR', desc: 'Average True Range - measures volatility', params: 'Period: 14' },
    bollinger_upper: { name: 'Bollinger Upper', desc: 'Upper volatility band', params: 'Period: 20, Std: 2' },
    bollinger_middle: { name: 'Bollinger Middle', desc: 'Middle band (SMA)', params: 'Period: 20, Std: 2' },
    bollinger_lower: { name: 'Bollinger Lower', desc: 'Lower volatility band', params: 'Period: 20, Std: 2' },
    keltner_upper: { name: 'Keltner Upper', desc: 'Upper Keltner Channel', params: 'Period: 20, Multiplier: 2' },
    keltner_lower: { name: 'Keltner Lower', desc: 'Lower Keltner Channel', params: 'Period: 20, Multiplier: 2' },
    donchian_upper: { name: 'Donchian Upper', desc: 'Highest high channel', params: 'Period: 20' },
    donchian_lower: { name: 'Donchian Lower', desc: 'Lowest low channel', params: 'Period: 20' },
    historical_vol: { name: 'Historical Volatility', desc: 'Standard deviation of returns', params: 'Period: 20' },
    
    // Volume (17)
    obv: { name: 'OBV', desc: 'On Balance Volume - cumulative volume', params: 'None' },
    ad: { name: 'A/D Line', desc: 'Accumulation/Distribution line', params: 'None' },
    cmf: { name: 'CMF', desc: 'Chaikin Money Flow', params: 'Period: 20' },
    vpt: { name: 'VPT', desc: 'Volume Price Trend', params: 'None' },
    nvi: { name: 'NVI', desc: 'Negative Volume Index', params: 'None' },
    pvi: { name: 'PVI', desc: 'Positive Volume Index', params: 'None' },
    force_index: { name: 'Force Index', desc: 'Price movement strength', params: 'Period: 13' },
    emv: { name: 'EMV', desc: 'Ease of Movement', params: 'Period: 14' },
    
    // Options/Greeks
    pcr: { name: 'Put-Call Ratio', desc: 'Market sentiment indicator. >1 bearish, <1 bullish', params: 'None' },
    delta: { name: 'Delta', desc: 'Option price sensitivity to underlying. Range: -1 to 1', params: 'None' },
    gamma: { name: 'Gamma', desc: 'Rate of Delta change. Highest near ATM', params: 'None' },
    theta: { name: 'Theta', desc: 'Time decay. Options lose value daily', params: 'None' },
    vega: { name: 'Vega', desc: 'Sensitivity to volatility. Long options benefit from IV rise', params: 'None' },
    iv: { name: 'Implied Volatility', desc: 'Market expectation of future volatility', params: 'None' },
    iv_rank: { name: 'IV Rank', desc: 'Current IV vs historical range (0-100)', params: 'Period: 252' },
    max_pain: { name: 'Max Pain', desc: 'Strike with most options expiring worthless', params: 'None' },
    dte: { name: 'Days to Expiry', desc: 'Time remaining until option expiration', params: 'None' },
  },
  
  // 60+ Candlestick Patterns
  patterns: {
    // Single Candle
    doji: { name: 'Doji', type: 'neutral', desc: 'Open and close are equal - indecision' },
    hammer: { name: 'Hammer', type: 'bullish', desc: 'Bullish reversal - long lower wick' },
    hanging_man: { name: 'Hanging Man', type: 'bearish', desc: 'Bearish reversal - appears at top' },
    inverted_hammer: { name: 'Inverted Hammer', type: 'bullish', desc: 'Bullish reversal - long upper wick' },
    shooting_star: { name: 'Shooting Star', type: 'bearish', desc: 'Bearish reversal - long upper wick' },
    spinning_top: { name: 'Spinning Top', type: 'neutral', desc: 'Indecision with small body' },
    marubozu_white: { name: 'Marubozu White', type: 'bullish', desc: 'Strong bullish candle - no wicks' },
    marubozu_black: { name: 'Marubozu Black', type: 'bearish', desc: 'Strong bearish candle - no wicks' },
    gravestone: { name: 'Gravestone Doji', type: 'bearish', desc: 'Bearish reversal - open/close at low' },
    dragonfly: { name: 'Dragonfly Doji', type: 'bullish', desc: 'Bullish reversal - open/close at high' },
    
    // Double Candle
    engulf_bullish: { name: 'Bullish Engulfing', type: 'bullish', desc: 'Second candle engulfs first - reversal up' },
    engulf_bearish: { name: 'Bearish Engulfing', type: 'bearish', desc: 'Second candle engulfs first - reversal down' },
    harami_bullish: { name: 'Bullish Harami', type: 'bullish', desc: 'Inside candle pattern - reversal up' },
    harami_bearish: { name: 'Bearish Harami', type: 'bearish', desc: 'Inside candle pattern - reversal down' },
    piercing: { name: 'Piercing Line', type: 'bullish', desc: 'Gap down then rallies to midpoint' },
    dark_cloud: { name: 'Dark Cloud Cover', type: 'bearish', desc: 'Gap up then falls below midpoint' },
    
    // Triple Candle
    morning_star: { name: 'Morning Star', type: 'bullish', desc: '3-day bullish reversal pattern' },
    evening_star: { name: 'Evening Star', type: 'bearish', desc: '3-day bearish reversal pattern' },
    three_white: { name: 'Three White Soldiers', type: 'bullish', desc: 'Three consecutive bullish candles' },
    three_black: { name: 'Three Black Crows', type: 'bearish', desc: 'Three consecutive bearish candles' },
    inside_bar: { name: 'Inside Bar', type: 'neutral', desc: 'Inside candle within previous range' },
    outside_bar: { name: 'Outside Bar', type: 'neutral', desc: 'Outside candle engulfs previous range' },
    
    // Advanced Patterns
    head_shoulders: { name: 'Head & Shoulders', type: 'bearish', desc: 'Reversal pattern with 3 peaks' },
    inverse_head_shoulders: { name: 'Inverse H&S', type: 'bullish', desc: 'Reversal pattern with 3 troughs' },
    double_top: { name: 'Double Top', type: 'bearish', desc: 'Two peaks at same level - reversal' },
    double_bottom: { name: 'Double Bottom', type: 'bullish', desc: 'Two troughs at same level - reversal' },
    cup_handle: { name: 'Cup & Handle', type: 'bullish', desc: 'Rounding bottom with pullback' },
    ascending_triangle: { name: 'Ascending Triangle', type: 'bullish', desc: 'Horizontal resistance, rising support' },
    descending_triangle: { name: 'Descending Triangle', type: 'bearish', desc: 'Horizontal support, falling resistance' },
    symmetrical_triangle: { name: 'Symmetrical Triangle', type: 'neutral', desc: 'Converging trendlines' },
    wedge_rising: { name: 'Rising Wedge', type: 'bearish', desc: 'Converging upward - breakdown expected' },
    wedge_falling: { name: 'Falling Wedge', type: 'bullish', desc: 'Converging downward - breakout expected' },
    flag_bullish: { name: 'Bull Flag', type: 'bullish', desc: 'Strong move with shallow pullback' },
    flag_bearish: { name: 'Bear Flag', type: 'bearish', desc: 'Strong drop with shallow rally' },
  },
  
  actions: {
    buy_ce: 'Buy Call Option - profits when price goes up',
    sell_ce: 'Sell Call Option - profits when price stays/goes down',
    buy_pe: 'Buy Put Option - profits when price goes down',
    sell_pe: 'Sell Put Option - profits when price stays/goes up',
    close: 'Close/Exit position',
  },
  
  // Multi-leg strategies
  strategies: [
    { name: 'RSI Reversal', desc: 'Buy when RSI < 30, Sell when RSI > 70' },
    { name: 'EMA Crossover', desc: 'Buy when EMA fast crosses above EMA slow' },
    { name: 'Supertrend Trend', desc: 'Buy when Supertrend turns green, Sell when red' },
    { name: 'MACD Signal', desc: 'Buy when MACD crosses above signal line' },
    { name: 'Bollinger Bounce', desc: 'Buy at lower band, Sell at upper band' },
    { name: 'VWAP Breakout', desc: 'Buy when price breaks above VWAP with volume' },
    { name: 'PCR Reversal', desc: 'Buy when PCR > 1.2 (fear), Sell when PCR < 0.7' },
    { name: 'IV Crush Strategy', desc: 'Sell options before earnings to capture IV crush' },
  ],
  
  // TradetrON signal values
  tradetron_signals: {
    '1': 'BUY - Enter long position',
    '2': 'SELL - Exit long position',
    '3': 'SHORT - Enter short position',
    '4': 'COVER - Exit short position',
  },
  
  tips: [
    'Always set Stop Loss before entering a trade',
    'Risk:Reward ratio of 1:2 or better is recommended',
    'Monitor IV before buying options - high IV means expensive premium',
    'Theta decay accelerates in last 2 weeks before expiry',
    'Use position sizing to limit risk per trade to 1-2%',
    'PCR > 1.2 indicates extreme fear - potential reversal opportunity',
    'Max Pain tends to attract price near expiry',
    'Buy theta when expecting low volatility (before weekend/holiday)',
  ],
};

const CopilotPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<{role: string; content: string}[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load history on mount
  useEffect(() => {
    const saved = localStorage.getItem('zobull_copilot_full_history');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setMessages(parsed.messages || []);
        setConversationHistory(parsed.history || []);
      } catch (e) {
        console.error('Failed to load history:', e);
      }
    } else {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: `🤖 **Welcome to ZO Bull AI Copilot!** 🐂

I'm your advanced AI trading assistant powered by ZO Bull v6.2.0. Here's what I can help you with:

📊 **274+ Technical Indicators**
- RSI, MACD, Stochastic, Bollinger, Supertrend, VWAP
- ADX, CCI, ATR, Ichimoku, and many more
- Just ask: "Explain RSI" or "How does MACD work?"

🕯️ **60+ Candlestick Patterns**
- Doji, Hammer, Engulfing, Morning Star
- Head & Shoulders, Cup & Handle, Double Top/Bottom
- "Show me bullish patterns" or "What is a shooting star?"

📈 **Options Trading**
- Put-Call Ratio (PCR) analysis
- Greeks: Delta, Gamma, Theta, Vega
- Max Pain, IV Rank, Expiry strategies
- "What does high PCR mean?" or "Explain theta decay"

🤖 **AI Signal Generation**
- Pattern recognition with confidence scores
- Multi-leg strategy builder
- "Find bullish patterns in NIFTY"

🔗 **TradetrON Integration**
- Signal values: 1=BUY, 2=SELL, 3=SHORT, 4=COVER
- Bi-directional trading setup
- "How to connect with TradetrON?"

🌐 **Multi-Language Support**
- English, Hindi, Hinglish, Tamil, Telugu
- Bengali, Marathi, Spanish, Chinese, Japanese
- "Kya hai RSI?" or "Supertrend kaise use karein?"

**Try asking:**
• "Create a strategy: Buy when RSI crosses 30 with SL 5%"
• "Explain the Greeks in simple terms"
• "What patterns are forming on NIFTY?"
• "How do I set up TradetrON?"

Just type your question or describe the strategy you want to create!`,
        timestamp: new Date()
      }]);
    }
  }, []);

  // Save history
  useEffect(() => {
    if (messages.length > 1) {
      localStorage.setItem('zobull_copilot_full_history', JSON.stringify({
        messages,
        history: conversationHistory
      }));
    }
  }, [messages, conversationHistory]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Intelligent response generator
  const generateResponse = (userQuery: string): string => {
    const query = userQuery.toLowerCase();
    
    // Indicator explanations
    for (const [key, data] of Object.entries(TRADING_KB.indicators)) {
      if (query.includes(key)) {
        return `📊 **${data.name}**

${data.desc}

**Parameters:** ${data.params}

**Trading Usage:**
- ${key === 'rsi' ? 'RSI > 70 = Overbought (potential sell), RSI < 30 = Oversold (potential buy)' : ''}
- ${key === 'ema' ? 'EMA crossover = trend change signals' : ''}
- ${key === 'supertrend' ? 'Green line = Uptrend, Red line = Downtrend' : ''}
- ${key === 'macd' ? 'MACD > Signal = Bullish, MACD < Signal = Bearish' : ''}
- ${key === 'bollinger' ? 'Price at upper band = potential resistance, lower band = support' : ''}
- ${key === 'adx' ? 'ADX > 25 = Strong trend, ADX < 20 = Sideways market' : ''}

Would you like me to create a strategy using ${data.name}?`;
      }
    }

    // Candlestick Pattern queries
    if (query.includes('pattern') || query.includes('candlestick') || query.includes('doji') || query.includes('hammer')) {
      const patterns = TRADING_KB.patterns;
      let response = '🕯️ **Candlestick Patterns**\n\n';
      response += 'I can detect 60+ candlestick patterns. Here are some:\n\n';
      
      if (query.includes('bullish')) {
        response += '**🟢 Bullish Patterns:**\n';
        Object.entries(patterns).filter(([k, v]) => v.type === 'bullish').forEach(([k, v]) => {
          response += `- *${v.name}*: ${v.desc}\n`;
        });
      } else if (query.includes('bearish')) {
        response += '**🔴 Bearish Patterns:**\n';
        Object.entries(patterns).filter(([k, v]) => v.type === 'bearish').forEach(([k, v]) => {
          response += `- *${v.name}*: ${v.desc}\n`;
        });
      } else {
        response += '**Single Candle:**\n';
        response += '- Doji: Open = close (indecision)\n';
        response += '- Hammer: Long lower wick (bullish reversal)\n';
        response += '- Shooting Star: Long upper wick (bearish reversal)\n\n';
        response += '**Double Candle:**\n';
        response += '- Engulfing: Second candle engulfs first\n';
        response += '- Harami: Inside candle pattern\n\n';
        response += '**Triple Candle:**\n';
        response += '- Morning Star: 3-day bullish reversal\n';
        response += '- Evening Star: 3-day bearish reversal\n\n';
        response += '**Advanced:**\n';
        response += '- Head & Shoulders: Reversal pattern\n';
        response += '- Cup & Handle: Continuation pattern\n';
        response += '- Double Top/Bottom: Key reversal signal\n';
      }
      
      response += '\nWant me to create a strategy based on a specific pattern?';
      return response;
    }

 
    // Options Trading queries (PCR, Greeks)
    if (query.includes("pcr") || query.includes("put-call") || query.includes("put call")) {
      return `📊 **Put-Call Ratio (PCR) Analysis**

PCR = Put Volume / Call Volume

**Interpretation:**
• PCR > 1.0: More puts being bought (bearish sentiment)
• PCR < 1.0: More calls being bought (bullish sentiment)
• PCR > 1.2: Extreme fear - potential reversal opportunity
• PCR < 0.7: Extreme greed - caution advised

**Trading Signals:**
• High PCR (>1.2) + Price near support = Potential BUY signal
• Low PCR (<0.7) + Price at resistance = Potential SELL signal

Want me to explain more about PCR?`;
    }   
    // Options Greeks queries
    if (query.includes("delta") || query.includes("gamma") || query.includes("theta") || query.includes("vega") || query.includes("greeks")) {
      return `📐 **Options Greeks**

**Delta (Δ)** - Price sensitivity
• Call Delta: 0 to +1, Put Delta: 0 to -1
• ATM Delta ≈ 0.5

**Gamma (Γ)** - Delta rate of change
• Highest for ATM options near expiry

**Theta (Θ)** - Time decay
• Options lose value daily, ATM has highest theta

**Vega (V)** - Volatility sensitivity
• Long options benefit from IV increase

Want me to show how to use Greeks in a strategy?`;
    }

    if (query.includes("max pain") || query.includes("max_pain")) {
      return `💀 **Max Pain Theory**

Max Pain = Strike where MOST options expire worthless.

**Trading:**
• Price below max pain = may push UP
• Price above max pain = may push DOWN

Want me to create a Max Pain trading strategy?`;
    }// 
    // TradetrON Integration queries
    if (query.includes("tradetron") || query.includes("tradtron")) {
      return `🔗 **TradetrON Integration**

ZO Bull supports bi-directional integration with TradetrON.

**Signal Values:**
• Signal 1 = BUY (Enter long position)
• Signal 2 = SELL (Exit long position)
• Signal 3 = SHORT (Enter short position)
• Signal 4 = COVER (Exit short position)

**Setup Steps:**
1. Enable TradetrON integration in Settings
2. Configure signal webhook URL
3. Map ZO Bull strategies to TradetrON signals

**Trading Flow:**
• ZO Bull generates signal based on strategy
• Signal sent to TradetrON via webhook
• TradetrON executes the trade

Want me to help set up TradetrON integration?`;
    }

    if (query.includes("webhook") || query.includes("signal")) {
      return `📡 **Trading Signals & Webhooks**

ZO Bull supports multiple signal types:

**Signal Types:**
• 1 = BUY
• 2 = SELL  
• 3 = SHORT
• 4 = COVER

**Webhook Configuration:**
• Set webhook URL in Settings > Integrations
• ZO Bull sends POST request with signal
• TradetrON receives and executes

**Example Webhook Payload:**
\`\`\`json
{
  "symbol": "NIFTY",
  "signal": 1,
  "price": 24500,
  "timestamp": "2026-06-17T08:00:00Z"
}
\`\`\`

Want me to explain webhook setup?`;
    };
    // Hindi/Hinglish queries
    if (query.includes('kya hai') || query.includes('kya hota') || query.includes('kaise')) {
      if (query.includes('rsi')) return '📊 **RSI (Relative Strength Index)**\n\nRSI ek momentum oscillator hai jo 0-100 ke beech mein hota hai.\n\n• RSI > 70 = Overbought (baazaar gir sakta hai)\n• RSI < 30 = Oversold (baazaar upar ja sakta hai)\n\n**Setting:** Period 14\n\n**Use:** Buy when RSI < 30, Sell when RSI > 70';
      
      if (query.includes('supertrend')) return '📈 **Supertrend**\n\nSupertrend ek trend-following indicator hai jo ATR use karta hai.\n\n• 🟢 Green = Uptrend (Buy signal)\n• 🔴 Red = Downtrend (Sell signal)\n\n**Settings:** Period 10, Multiplier 3\n\n**Trading:** "Supertrend green ho toh CE kharido, red ho toh PE"';
      
      if (query.includes('ema') || query.includes('moving average')) return '📉 **Moving Averages (EMA/SMA)**\n\nYe price ka average hote hain jo trend dikhate hain.\n\n• EMA = Recent prices ko zyada weight deta hai\n• SMA = Saare prices equal weight\n\n**Common Settings:**\n• Short term: 9, 21 EMA\n• Long term: 50, 100, 200 EMA\n\n**Signal:** Jab fast EMA slow EMA ko cross kare';
    }

    // Strategy creation
    if (query.includes('create') || query.includes('banao') || query.includes('banana') || query.includes('strategy')) {
      return `📝 **Strategy Creation Mode**

Mujhe batao aap kya strategy banana chahte hain:

**Format (English):** "Buy [instrument] when [condition] with SL [X%] TP [Y%]"

**Format (Hinglish):** "[Indicator] [condition] toh [action] karo SL [X]% TP [Y]%"

**Examples:**
• "Buy NIFTY CE when RSI crosses above 70 with SL 5% TP 15%"
• "Supertrend bullish ho toh PE sell karo SL 8% TP 20%"
• "EMA 20, EMA 50 ko cross kare toh buy karo"

**Quick Templates:**
${TRADING_KB.strategies.map(s => `• ${s.name}: ${s.desc}`).join('\n')}

Kaunsa strategy banana hai?`;
    }

    // Market info
    if (query.includes('market') && (query.includes('time') || query.includes('hour') || query.includes('timing'))) {
      return `🕐 **Market Timings (IST)**

**🇮🇳 Indian Markets:**
• Pre-Open: 9:00 AM - 9:15 AM
• Regular: 9:15 AM - 3:30 PM
• Post-Close: 3:30 PM - 4:00 PM

**🌐 Crypto Markets:**
• 24/7 Open (Global markets)

**⏰ Best Trading Times:**

**Intraday (Options):**
• 9:15-10:00 AM: High volatility, best for momentum
• 10:00-12:00 PM: Trend confirmation
• 12:00-2:00 PM: Consolidation
• 2:00-3:00 PM: Volatility increases

**Swing Trading:**
• After 2:30 PM for trend confirmation
• Expiry days (Thursday): High volatility

**💡 Tips:**
• Avoid trading during low liquidity (12-1 PM)
• Post-market best for analysis`;
    }

    // Risk management
    if (query.includes('stop loss') || query.includes('sl') || query.includes('stoploss') || query.includes('risk')) {
      return `🛡️ **Risk Management**

**Stop Loss (SL):**
• Options: 5-10% of premium
• Futures: 1-2% of contract value
• Always set before entering!

**Position Sizing:**
• Risk per trade: 1-2% of capital
• Formula: Position Size = Risk Amount / SL %

**Take Profit (TP):**
• Minimum Risk:Reward = 1:2
• Example: SL 5% → TP minimum 10%

**Key Rules:**
1. Never risk more than 2% per trade
2. Use trailing stop for locked profits
3. Adjust position size based on volatility (use ATR)
4. Never average losing positions

**Position Size Example:**
• Capital: ₹1,00,000
• Risk per trade: 2% = ₹2,000
• SL: 5% = ₹2,000 / 0.05 = ₹40,000 position size
• If NIFTY lot size = 25, buy 1 lot @ ₹40,000`;
    }

    // Greetings
    if (query.match(/^(hi|hello|hey|namaste|namaskar|greetings)/)) {
      return '🙏 Namaste! Welcome to ZO Bull AI Copilot!\n\nI\'m your trading assistant. I can help you:\n• Create trading strategies\n• Explain indicators\n• Answer market questions\n\nWhat would you like to know?';
    }

    // Tips
    if (query.includes('tip') || query.includes('advice') || query.includes('sugges')) {
      const tip = TRADING_KB.tips[Math.floor(Math.random() * TRADING_KB.tips.length)];
      return `💡 **Trading Tip of the Day**\n\n${tip}\n\nNeed more tips or have specific questions?`;
    }

    // Options basics
    if (query.includes('option') && (query.includes('what') || query.includes('basics') || query.includes('kya'))) {
      return `📚 **Options Trading Basics**

**Call Option (CE):**
• Buy when you think price will go UP
• Profit = (Exit Price - Entry Price) × Lot Size
• Max loss = Premium paid

**Put Option (PE):**
• Buy when you think price will go DOWN
• Profit = (Entry Price - Exit Price) × Lot Size
• Max loss = Premium paid

**Key Terms:**
• ATM: Strike = Current Price (highest gamma)
• ITM: Strike below (CE) / above (PE) current price
• OTM: Strike above (CE) / below (PE) current price

**Premium Factors:**
• Intrinsic Value + Time Value (Theta)
• IV High = Expensive premium
• Theta decay increases near expiry

Want to learn about specific strategies?`;
    }

    // Default response
    return `🤔 I'm here to help with your trading questions!

**Try these:**
• "Explain RSI" or "RSI kya hai?"
• "Create a momentum strategy"
• "Stop loss kaise set karein?"
• "Market timing kya hai?"

I support **42 languages**: English 🇺🇸, Hindi 🇮🇳, Hinglish 🗣️, Tamil 🇮🇳, Telugu 🇮🇳, Kannada 🇮🇳, Malayalam 🇮🇳, Bengali 🇮🇳, Marathi 🇮🇳, Gujarati 🇮🇳, Punjabi 🇮🇳, Odia 🇮🇳, Assamese 🇮🇳 + 14 more Indian languages 🇮🇳, Spanish 🇪🇸, Chinese 🇨🇳, Japanese 🇯🇵, Korean 🇰🇷!`;
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMsg: Message = {
      id: generateId(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    
    // Add to conversation history for context
    setConversationHistory(prev => [...prev, { role: 'user', content: userMsg.content }]);
    
    setInputValue('');
    setIsLoading(true);

    const assistantMsg: Message = {
      id: generateId(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true
    };
    setMessages(prev => [...prev, assistantMsg]);

    try {
      // Try streaming API
      const response = await fetch('/api/ai-copilot/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMsg.content,
          context: { history: conversationHistory }
        }),
      });

      if (response.ok && response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          fullContent += decoder.decode(value);
          setMessages(prev => prev.map(m => 
            m.id === assistantMsg.id ? { ...m, content: fullContent } : m
          ));
        }

        setMessages(prev => prev.map(m => 
          m.id === assistantMsg.id ? { ...m, content: fullContent, isStreaming: false } : m
        ));
        setConversationHistory(prev => [...prev, { role: 'assistant', content: fullContent }]);
      } else {
        // Fallback to local generation
        const responseText = generateResponse(userMsg.content);
        
        setMessages(prev => prev.map(m => 
          m.id === assistantMsg.id ? { ...m, content: responseText, isStreaming: false } : m
        ));
        setConversationHistory(prev => [...prev, { role: 'assistant', content: responseText }]);
      }
    } catch (error) {
      // Offline fallback
      const responseText = generateResponse(userMsg.content);
      
      setMessages(prev => prev.map(m => 
        m.id === assistantMsg.id ? { ...m, content: responseText, isStreaming: false } : m
      ));
      setConversationHistory(prev => [...prev, { role: 'assistant', content: responseText }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([{
      id: generateId(),
      role: 'assistant',
      content: '🗑️ Chat cleared!\n\nHow can I help you with trading today?',
      timestamp: new Date()
    }]);
    setConversationHistory([]);
    localStorage.removeItem('zobull_copilot_full_history');
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="copilot-page">
      <div className="copilot-page-container">
        {/* Sidebar */}
        <div className="copilot-sidebar">
          <div className="sidebar-header">
            <h2>🤖 AI Copilot</h2>
            <span className="version">v5.24.0</span>
          </div>
          
          <div className="sidebar-section">
            <h3>📊 Indicators</h3>
            <div className="indicator-list">
              {Object.entries(TRADING_KB.indicators).map(([key, data]) => (
                <button 
                  key={key}
                  onClick={() => setInputValue(`Explain ${data.name}`)}
                  className="indicator-btn"
                >
                  {data.name}
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <h3>📝 Quick Strategies</h3>
            <div className="strategy-list">
              {TRADING_KB.strategies.map((s, i) => (
                <button 
                  key={i}
                  onClick={() => setInputValue(`Create ${s.name} strategy`)}
                  className="strategy-btn"
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <h3>💡 Trading Tips</h3>
            <div className="tips-list">
              {TRADING_KB.tips.map((tip, i) => (
                <div key={i} className="tip-item">• {tip}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="copilot-chat-area">
          <div className="chat-header">
            <div className="chat-title">
              <span className="icon">🤖</span>
              <div>
                <h3>ZO Bull AI Copilot</h3>
                <span className="status">Online • Multi-language Support</span>
              </div>
            </div>
            <div className="chat-actions">
              <button onClick={clearChat} className="clear-btn" title="Clear Chat">
                🗑️ Clear
              </button>
            </div>
          </div>

          <div className="chat-messages">
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`message ${message.role} ${message.isStreaming ? 'streaming' : ''}`}
              >
                <div className="message-avatar">
                  {message.role === 'assistant' ? '🤖' : '👤'}
                </div>
                <div className="message-content">
                  <div className="message-text">
                    {message.content.split('\n').map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                  <div className="message-meta">
                    <span className="timestamp">{formatTime(message.timestamp)}</span>
                    {message.isStreaming && <span className="typing">typing...</span>}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-area">
            <div className="language-hint">
              Supports: 42 languages 🇺🇸🇮🇳🇪🇸🇨🇳🇯🇵
            </div>
            <div className="chat-input-row">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask about trading strategies, indicators, or risk management..."
                disabled={isLoading}
                rows={1}
              />
              <button 
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading}
                className="send-btn"
              >
                {isLoading ? '⏳' : '➤'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopilotPage;